<template>
  <div class="x-editor" :class="{ 'x-editor-mode-edit': mode, editing }">
    <div v-show="mode == 'edit' || editing" class="x-editor-menu">
      <img
        v-for="(tagLabel, tagName) in taglist"
        class="x-editor-tag"
        :data-var="tagName"
        :data-label="tagLabel"
        :src="tagLabel"
        :key="tagName"
        @mousedown.prevent="insert(tagName)"
      />
    </div>
    <div
      class="x-editor-paper"
      :class="{ editing: mode == 'edit' || editing }"
      v-html="xhtml"
      contenteditable="true"
      @keydown.enter.prevent="save"
      @keydown.esc.prevent="cancel"
    ></div>
  </div>
</template>

<script>
/**
 * 使用方式：
 *
 * 双向绑定数据，默认是展现模式，点击内容变成编辑模式
 * <Editor v-model="pattern">
 *
 * 始终是编辑模式
 * <Editor v-model="pattern" mode="edit">
 *
 * 指定标签列表（推荐），要求是object对象，key是模板变量名，value是占位符icon图片地址
 * <Editor v-model="pattern" :labels="['userNick', 'anchorNick']">
 *
 * 指定标签列表（已弃用，但仍然支持），要求是object对象，key是模板变量名，value是占位符icon图片地址
 * <Editor v-model="pattern" :tags="{userNick: '图片url'}">
 */
const getDefaultTags = () => ({
  // 主播昵称
  anchorNick: require("@/assets/pic/x-editor/tag1.png"),
  // 粉丝昵称
  userNick: require("@/assets/pic/x-editor/tag2.png"),
  // 礼物
  gift: require("@/assets/pic/x-editor/tag3.png"),
  // 礼物存量
  walletGift: require("@/assets/pic/x-editor/tag4.png"),
  // 奖品
  award: require("@/assets/pic/x-editor/tag5.png"),
  // 陪玩技能
  accompanySkill: require("@/assets/pic/x-editor/tag6.png"),
  // 贵族等级
  noble: require("@/assets/pic/x-editor/tag7.png"),
  //守护等级
  guardLevel: require("@/assets/pic/x-editor/tag8.png"),
  // 粉丝等级
  badge: require("@/assets/pic/x-editor/tag10.png"),
  // 马甲格式
  nickPrefix: require("@/assets/pic/x-editor/tag9.png"),
  // 陪玩技能
  accompanyTarget: require("@/assets/pic/x-editor/tag11.png"),
  // 消费档暗号
  consumeSignal: require("@/assets/pic/x-editor/tag12.png"),
  // 签到天数
  checkInDays: require("@/assets/pic/x-editor/tag13.png"),
});

export default {
  name: "XEditor",
  props: {
    value: String, // v-model双向绑定
    mode: String, // 编辑器模式，默认是编辑+展示，可选值：edit，仅编辑
    labels: Array,
    tags: Object,
  },
  data() {
    return {
      editing: false,
    };
  },
  mounted() {
    // 保存一下dom
    this.paper = this.$el.querySelector(".x-editor-paper");

    // 把字符串转成html，加上占位
    // this.paper.innerHTML = this.text2html(this.value);

    // 失去焦点自动保存
    this.paper.addEventListener("focus", () => {
      this.editing = true;
    });
    // 失去焦点自动保存
    this.paper.addEventListener("blur", () => {
      this.save();
      this.editing = false;
    });
    // this.syncTags();
  },
  computed: {
    taglist() {
      let tags = {};
      if (this.labels && this.labels.length) {
        const DEFAULT_TAGS = getDefaultTags();
        this.labels.forEach((label) => {
          if (DEFAULT_TAGS.hasOwnProperty(label)) {
            tags[label] = DEFAULT_TAGS[label];
          }
        });
      } else if (this.tags && Object.keys(this.tags).length) {
        tags = Object.assign({}, this.tags);
      } else {
        tags = getDefaultTags();
      }
      return tags;
    },
    xhtml() {
      return this.text2html(this.value);
    },
  },
  methods: {
    // 插入标签
    insert(name) {
      // 选区操作
      var session = window.getSelection();
      if (!this.editing) {
        this.paper.focus();
        const length = this.paper.childNodes.length;
        if (length) {
          const range = session.getRangeAt(0);
          range.setStart(this.paper, length);
        }
      }
      const label = this.taglist[name];
      if (label) {
        const range = session.getRangeAt(0);
        // 判断是不是光标在paper里
        const image = new Image();
        image.className = `x-editor-tag x-editor-inline`;
        image.setAttribute("data-var", name);
        image.setAttribute("data-label", label);
        image.src = label;
        range.insertNode(image);
        range.setStartAfter(image);
      }
    },

    // 模板转html
    text2html(pattern) {
      pattern = String(pattern || "");
      return pattern.replace(/\$\{([^\}]+)\}/g, (m, name) => {
        const label = this.taglist[name];
        if (label) {
          return `<img class="x-editor-tag x-editor-inline" data-var="${name}" src="${label}"/>`;
        }
        return m;
      });
    },

    // 取消编辑
    cancel() {
      if (!this.mode) {
        this.editing = false;
        this.paper.innerHTML = this.text2html(this.value);
      }
    },

    // 应用编辑
    save() {
      this.editing = false;
      const dom = this.paper.cloneNode(true);
      dom.querySelectorAll("[data-var]").forEach((node) => {
        const placeholder = node.getAttribute("data-var");
        const text = document.createTextNode("${" + placeholder + "}");
        node.parentNode.replaceChild(text, node);
      });
      this.$emit("input", dom.textContent);
    },
  },
};
</script>

<style>
.x-editor {
  font-size: 14px;
  position: relative;
  outline: none;
  transition: box-shadow 0.3s ease-out;
  text-align: left;
}
.x-editor-tag {
  display: inline;
}
.x-editor-paper {
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}
.x-editor-paper:hover {
  background: rgba(255, 255, 0, 0.1);
}

.x-editor-paper:focus {
  outline: none;
}

.x-editor-paper.editing {
  background: rgba(255, 255, 0, 0.15);
  box-shadow: inset 0 -2px 5px 0 rgba(0, 0, 0, 0.1);
}

.x-editor-menu {
  background-color: rgb(84, 92, 100);
  padding: 7px 5px;
  position: absolute;
  bottom: 100%;
  width: 100%;
  font-size: 12px;
  z-index: 2;
}

.x-editor-mode-edit .x-editor-menu {
  position: static;
}

.x-editor-menu:before {
  content: "点击插入➜";
  color: #ccc;
  margin-right: 5px;
}

.x-editor-paper {
  padding: 5px;
  cursor: text;
  min-height: 24px;
  line-height: 24px;
}
.x-editor-tag {
  display: inline-block !important;
  margin: 0 2px;
  background: none;
  border: none;
  height: 18px;
  vertical-align: -4px;
}

.x-editor-menu .x-editor-tag {
  cursor: pointer;
}
</style>
